import {observable, autorun, computed, toJS} from 'mobx';
import ServerAPI from '../ServerAPI';
import Machine from './Machine';
import Location from './Location';
import * as LoadLevel from './LoadLevel';
import User from './User';
import Customer from './Customer';
import Contract from './Contract';
import SimCard from './SimCard';
import AuditLogRequest from "./AuditLogRequest";
import moment from "moment/moment";

// uncomment next line to enable the dev-tools.
//import 'mobservable-react-devtools';

/**
  Any data registration operations that require us to send a request to the
  server for insertion of data into the database, needs to provide feedback
  to the user.  The ServerOperation class captures a standard set of status
  fields that will be used to render the UI.
  TODO : Remove this class.  A less complicated solution with global status
  flags have been used to capture ongoing server-operations in stead.
*/
class ServerOperation {
  // If we are waiting for server to reply.  Use to show loading indicator
  // and deactivate/remove the UI element that triggered the update
  @observable waiting = false;
  // When the server comes back this will hold the result of the INSERT
  // E.g. OK, TIMEOUT, FAIL.  null means request not sent
  @observable result = null;
  // The message returned from the server for display to the user.
  @observable message = null;

  reset() {
    this.waiting=false;
    this.result=null;
    this.message=null;
  }
}

/**
  Data regarding presentation and configuration of the UI
  we collect under this namespace.  E.g. whether to show super-user
  menu choices or to auto-hide the navigation menu or keep it open.
  Having this as its own class is only to keep the application state
  better organized.
*/
class UISessionState {
  // The number of pixels, this variable is set by the AppLayout component
  // which will listen to the required DOM element events.
  @observable deviceWidth = 0;
  // And similarly the heigth of the visible area to use:
  @observable deviceHeight = 0;
  // Density of pixels vs. dips
  @observable pixelRatio = 1;
  // Variable that is set and unset by users clicking on navigation menu
  // for SMALL device compact UI
  @computed get deviceWxHStr() {
    return this.deviceWidth+"x"+this.deviceHeight+"@"+this.pixelRatio;
  }
  @observable showNavigation = false;
  // if fullscreen is set to true, then the current page will be rendered
  // without top menu or nav menu
  @observable fullScreen = false;
  // Based on the with of the device, we compute a ui size category:
  @computed get uiSize() {
    if (this.deviceWidth >= 992) {
      return "LARGE";
    } else if (this.deviceWidth >= 768) {
      return "MEDIUM";
    } else { // width < 768
      return "SMALL";
    }
  }
  /* When fixedNavigation is true, we always show the navbar
     and hide the open-nav menu button. */
  @computed get fixedNavigation() {
    if(this.uiSize!="SMALL") return true;
    return false;
  }

  // True if the navigation is showing :
  @computed get navigationShowing() {
    return this.fixedNavigation || this.showNavigation;
  }

  // Page specific state, such as show/hide toggles:
  @observable MachinePage_showMachineDetails = false;

}

// A list of parameters that control some behaviour of
// a deconx machine.
class MachineConfigurationForm {
  // An array of MachineConfigParameters
  @observable machine;
  @observable parameters=[];
  reset() {
    this.machine=null;
    this.parameters=[];
  }
}

// All data required to register a new machine in the system
class MachineForm {
  machine = new Machine();
  // The search string used to lookup
  id_customer_search = "";
  // Response from server on attempted insert
  serverOperation = new ServerOperation();

  // Make a list of presentable strings for client to select from
  searchTextFromCustomerEntry(entry) {
    return entry.name+" ("+entry.id+")";
  }
  @computed get customerSelectList() {
    return appState.customerList.map(this.searchTextFromCustomerEntry);
  }
  // set the id_customer based on selected value in drop-down list
  selectCustomer(selected) {
    // Iterate the customerList until we find the selected item
    // was from it, and if so, pick the id of the selected customer
    if(!appState.customerList.some((entry) => {
      if(this.searchTextFromCustomerEntry(entry)==selected) {
        this.machine.id_customer = entry.id;
        return true;
      }
      return false;
    })) {
    // If we could not find a match in the customer list, e.g. userId
    // hit enter without having selected a valid customer:
      this.machine.id_customer = null;
    }
  }
}


// All data required to register a new machine in the system
class MachineAssignmentForm {

  // The machine object to fill in
  @observable machine;
  // The customer object to assign machine to
  @observable customer;
  // The search string used to lookup customer to assign machine to
  @observable customer_search_text = "";

  constructor() {
    this.machine= new Machine();
    this.customer= null;
  }
}

// All data required to search for machines
// For now the only search parameter is which customer to list machines for
class MachineSearchForm {
  // The search string used to lookup customer to assign machine to
  @observable customer_search_text = "";
  // The selected customer
  @observable customer = null;
}

// Data to insert or edit a customer
class CustomerForm {
  // Object that holds new values for new/existing customer
  @observable customer;
  // Response from server on attempted insert
  @observable serverOperation; // = new ServerOperation();
  // For new customer registration, we also register the initial admin user
  @observable adminUser; // type:UserForm;
  constructor() {
    this.reset();
  }
  reset() {
    this.customer = new Customer();
    this.serverOperation = new ServerOperation();
    this.adminUserForm = new UserForm();
  }
}

// Data to insert or edit contract data
class ContractForm {
  // Object that holds new values for new/existing customer
  @observable contract;
  // Response from server on attempted insert
  @observable serverOperation;
  // To display the relevant machine on the contract form:
  @observable machine;
  // To display the relevant customer name on the contract form:
  @observable customer;
  // The last date of existing support contracts we found.
  @observable last_support_till;

  constructor() {
    this.reset();
  }
  reset() {
    this.contract = new Contract();
    // These blank objects avoids null exceptions in JSX form rendering
    // before real data is filled in:
    this.machine = new Machine();
    this.customer = new Customer();
    this.customer = new Customer();
    this.serverOperation = new ServerOperation();
    this.last_support_till = null;
  }
}

// All data required to insert a new user
class UserForm {
  // Object to hold all field values of the new/update form
  @observable user;
  // This field can be used to check if the phone number has been updated
  // in which case the phone_confirmed field should be set to false after the
  // update
  @observable prev_phone;
  // Field to hold the original value of the email field
  @observable prev_email;
  // Field to hold the original value of the id field
  @observable prev_id;
  // The set of roles the user had before we started editing:
  @observable prev_roles;
  // Response from server on attempted insert
  @observable serverOperation; // = new ServerOperation();
  // If we are in the process of running a user-name taken? query
  @observable checkingAvailability = false;
  // If we are in the process of running a check of the email uniqueness:
  @observable checkingUniqueEmail = false;

  constructor() {
    this.user = new User();
    this.serverOperation = new ServerOperation();
    this.prev_roles = new Set();
  }

  reset() {
    this.serverOperation.reset();
    this.user = new User();
    this.prev_phone=null;
    this.prev_email=null;
    this.prev_id=null;
    this.userNameTaken = false;
    this.prev_roles = new Set();
  }

  // True if the email has been changed :
  @computed get emailChanged() {
    return this.prev_email != this.user.email;
  }

  // True if the email has been changed :
  @computed get idChanged() {
    return this.prev_id != this.user.id;
  }

  // True if the phone number has been changed :
  @computed get phoneChanged() {
    return this.prev_phone != this.user.phone;
  }
}

// All data related to requesting a audit log based on parameters
class AuditLogSearchForm {
  // The request record to pass on to the server when ready.
  @observable request; // = new ReportRequest();

  // When request completed or cancelled etc :
  constructor() {
    this.request = new AuditLogRequest();
    this.request.date_from = moment().startOf('day').toDate();
    this.serverOperation = new ServerOperation();
  }

  reset() {
    this.request = new AuditLogRequest();
    this.request.date_from = moment().startOf('day').toDate();
  }
}

// All data required to insert a new room into the location structure
class RoomForm {
  // Object to hold all field values of the new/update form
  @observable location;// = new Location();
  // Response from server on attempted insert
  @observable serverOperation;// = new ServerOperation();

   constructor() {
     this.location = new Location();
     this.serverOperation = new ServerOperation();
   }

  reset() {
    this.serverOperation.reset();
    this.location.reset();
    if(appState.userInfo && appState.userInfo.id_home_location) {
      this.location.id_parent = appState.userInfo.id_home_location;
    }
    else {
      this.location.id_parent = "root";
    }
    // Check if we have >1 job_types and need to set a default :
    if(appState.availableJobTypes.length>1) {
      this.location.default_job_type=appState.availableJobTypes[0].id;
      // NB This fails on reload as job_types will load after this init happened
      // The current solution is running the method
      // fixDefaultJobTypesForRooms after completing load of job_types.  It will
      // initialize the default_job_type field for the singe instance of
      // this form.
    }

  }

  duplicateReset() {
    this.serverOperation.reset();
    this.location.beacons = [];
    this.location.id = null;
    this.location.path = null;
    this.last_disinfected = null;
    this.new_beacon_id = null;
  }
  
  // We leave the parent and room-type selection to make it easier
  // to register several rooms under the same area:
  clearForNext() {
    if(this.location.id_parent==null) {
      this.location.id_parent = "root";
    }
    this.serverOperation.reset();
    this.location.name=null;
    this.location.volume_m3=null;
    this.location.width=null;
    this.location.length=null;
    this.location.beacons=[];
    this.location.new_beacon_id=null;
  }

  // Empty enough to show Done in stead of Cancel
  @computed get isEmpty() {
      return this.name==null && this.volume_m3 == null ;
  }
}

/**
  User preferences are organized in this class
*/
class UsersPreferences {
  @observable jobCompleteSMS=false;
  @observable alarmSMS=false;
  @observable preferencesLoading=false;
}

/**
  SIM configuration form
*/
class SimCardForm {
  // Model which holds all information
  @observable simCard;
  @observable newFreeze;
  @observable newDataPackage;
  @observable newSMSPackage;
  @observable newMachineId;
  @observable newRoaming;

  constructor() {
    this.simCard = new SimCard();
    this.newFreeze = null;
    this.newDataPackage = null;
    this.newSMSPackage = null;
    this.newMachineId = null;
    this.newRoaming = null;
  }

  reset() {
    this.simCard = new SimCard();
    this.newFreeze = null;
    this.newDataPackage = null;
    this.newSMSPackage = null;
    this.newMachineId = null;
    this.newRoaming = null;
  }
}

/**
  SIM configuration form
*/

/**
  This Object captures the entire state of the application.
  The UI consists of compositions of rendered data from this state object
  Only observable data and functions (recomputed on change), should be
  put into this structure.
  Utility functions that impose change to this structure goes into the
  AppModelUtils module

  There should only be one instance of this Class, called "appState"
  which will be exported as AppModel to other modules

*/
class AppState {
    @observable loggedIn=false;
    @observable login_email = null;
    @observable login_email_verified = false;
    @observable impersonator = null;

    // If the user has the ability to log in, but no user profile is
    // connected to the log-in, we will show a registration-flow-step, or
    // just information about becoming a user.
    @observable missingUserProfile=false;
    @observable uiSession;
    @observable title="Decon-X";
    // To enable language change to also trigger the title change :
    @observable titleId="PT_DECONX";

    // Which Navigation item to highlight in menu
    // This is set when the route changes (and possibly also when nav clicked)
    @observable selectedNavLinkIndex="home";
    // Users are identified by a username unique within a given customer:
    @observable id_customer=null;
    // When a customer admin is logged in and operates on a selected customer,
    // this field is used to hold the ID of the currently selected customer.
    // TODO : Remove this, I do not believe we ever added super-admin-login with
    // customer-selection to the app.
    @observable id_admin_customer=null;
    @observable id_user=null;
    @observable fetchingUserProfile=false;
    // Not sure where this should be set, perhaps on the user profile in the DB
    @observable time_zone = 'Europe/Oslo';
    @observable locale = 'en-GB'

    @observable userInfo=null; // Object with row from user table
    // On login, we load permissions and roles which may influence UI
    @observable permissions=[];
    @observable roles=[];
    // uiMessage will be displayed for a few seconds at the bottom of the UI
    @observable uiMessage=null;
    // If the uiUndoAction is set, then instead of OK a UNDO will be displayed
    // in the pop-over and clicking that will run the action.
    @observable uiUndoAction=null;
    @observable uiMessageDelay=5000;
    // uiError will be displayed as a modal dialogue the user must "OK"
    @observable uiError=null;

    @observable users_prefs; // = new UsersPreferences();

    @observable machineList=[];
    // When looking at the machine-detail page, what we are rendering is the
    // "current machine":
    @observable currentMachine=null;
    @observable currentMachineLoading=null;
    @observable fetchingMachineList=false;
    @observable machineListLastFetched=null;

    // List of users to show on the admin page :
    @observable userList=[];
    @observable fetchingUserList=false;
    @observable auditLog=[];
    @observable fetchingAuditLog=false;
    // Forms used to insert or edit a user.
    @observable newUserForm;// = new UserForm();
    // Set this true while waiting for server data:
    @observable fetchingEditUserDetails = false;
    @observable updateUserForm;// = new UserForm();

    @observable auditLogSearchForm;// = new AuditLogSearchForm();

    @observable customerList=[];
    @observable nextCustomerId=null;
    @observable customerListIncludingOwnCompany=[];
    // Set this true while waiting for server data:
    @observable customerListLoading=false;
    @observable fetchingCustomerDetails = false;
    @observable updatingCustomerDetails = false;
    @observable updateCustomerForm;
    // Used when creating a new customer entry
    @observable newCustomerForm;
    // To insert a new machine, fill in the form fields and
    // run the method registerNewMachine
    @observable newMachineForm=new MachineForm();

    // Should be true while we are loading details from DB about a machine
    @observable fetchingMachineDetails = false;
    // Should be true while we are looking up the sim card activation status for a machine
    @observable fetchingSimActivationStatus = false;
    // True if a server-operation is in progress to verify new customer id
    @observable checkingCustomerId=false;
    // List of possible customer segments (from database)
    @observable customer_segments = [];
    @observable fetching_customer_segments = false;

    // List of possible job types (from database)
    @observable job_types = [];
    @observable fetching_job_types = false;

    // True while we are waiting for server side to check verification code
    @observable sendingPhoneVerificationCode=false;
    @observable verifyingPhoneOperation=false;

    // When looking at the job-detail page, what we are rendering is the
    // "current job":
    @observable currentJob=null;
    @observable fetchingJobDetails=null;
    @observable timeStampLastJobUpdate=null;

    @observable jobList=[];
    @observable lastFetchedJobListTimestamp=null;
    @observable sortedJobList=[];
    @observable fetchingJobList=false;
    @observable fetchingMoreJobs=false;
    @observable jobListLastFetched=null;
    @observable fetchingDashboardData=false;
    // A list of the locations relevant to the currently logged in user.
    @observable currentLocation=null;
    @observable userLocations=[];
    @observable fetchingLocationData=false;
    // List of valid location types:
    @observable locationTypes=[]; // [{id,name,description},{...},...]
    @observable fetchingLocationTypes=false;
    // List of beacons assigned to customer
    @observable customer_beacons=[];
    @observable fetchingBeaconData=false;
    // Data filled in that will be used to create a new room :
    @observable newRoomForm;
    @observable updateRoomForm;
    // When adding a new user or editing an existing, we need to know which
    // roles current user is allowed to grant to others :
    @observable grantableRoles=[];
    @observable fetchingGrantableRoles = false;
    @observable fetchingImpersonateToken=false;
    // Job types available for this customer
    // Used to set default values for locations.
    @observable fetchingJobTypes=false;

    @observable fetchingAvailableJobTypes=false;
    @observable availableJobTypes=[];

    // The list of recently deleted users or locations :
    // To implement a recovery / undo feature
    @observable deletions = null;
    @observable fetchingDeletions = false;
    @observable restoringDeletion = false;

    // Flag to indicate if we are waiting for confirmation from server
    // about request for email report resend:
    @observable requestingEmailReport = false;

    // When we have sent a request for updated job-status data
    // and are still waiting for reply, this will be true:
    @observable fetchingJobUpdates = false;

    // Form data for editing machine configuration:
    @observable machineConfigurationForm;
    @observable updatingMachineConfig = false;
    @observable resettingMachineConfig = false;

    // for displaying progress waiting for SMS to be sent
    @observable sendingTestSMS = false;

    // The list of supported countries that a customer may be tagged with
    @observable fetchingSupportedCountries=false;
    @observable supportedCountries=[];
    // List of supported languages that the UI can be display in
    @observable supportedLanguages = [];
    @observable fetchingSupportedLanguages=false;

    // Search fields for finding a subset of all machines in the system:
    @observable machineSearchForm = null;
    @observable machineSearchResult = [];
    @observable runningMachineSearch = false;

    // Form used to fill in new customer and machine name when re-assigning
    // machines to new customers:
    @observable machineAssignmentForm = null;
    @observable updatingMachineAssignment=false;
    @observable fetchingMachineName = false;
    // Map of id_customer:id_machine => name
    @observable customerMachineNames = null; // new Map();

    // List of contracts for a given customer/machine combo:
    @observable contractList = [];
    @observable contractListLoading = false;
    @observable contractDetailLoading = false;
    @observable contractForm = null;
    @observable fetchingContractFormContext = false;
    @observable updatingContractData = false;

    // Sim card activation/deactivation ongoing.
    @observable changingSIMCardActivationStatus = false;

    @observable fetchingSimCards = false;
    @observable simCards = [];
    @observable roamingStatus = null;
    @observable sortField = 'status';
    @observable direction = true; 
    @observable simCardForm; // = new SimCardForm();
    @observable dataSubscriptions = [];
    @observable smsSubscriptions = [];
    @observable searchTerm = null;
    
    @observable simCardUsage = [];
    @observable simCardLocationData = null;
    @observable fetchingSimCardDetails = false;
    @observable updatingSimCard = false;


    // Initialize all observable object-properties.
    // If they are initialized  with the @observable decorator,
    // they are made observable lazily, which triggers exceptions if it
    // is caused by a React render method.  So to be on the safe side :
    constructor() {
      this.uiSession = new UISessionState();
      this.users_prefs = new UsersPreferences();
      this.newUserForm = new UserForm();
      this.updateUserForm = new UserForm();
      this.auditLogSearchForm = new AuditLogSearchForm();
      this.newRoomForm = new RoomForm();
      this.updateRoomForm = new RoomForm();
      this.machineConfigurationForm = new MachineConfigurationForm();
      this.updateCustomerForm = new CustomerForm();
      this.newCustomerForm= new CustomerForm();
      this.machineSearchForm = new MachineSearchForm();
      this.machineAssignmentForm = new MachineAssignmentForm();
      this.customerMachineNames = new Map();
      this.contractForm = new ContractForm();
      this.simCardForm = new SimCardForm();
    }

    // Whenever the user e.g. deletes something, we should
    // invalidate the previously loaded list
    clearDeletionsListForReload() {
      this.deletions = null;
      this.fetchingDeletions = false;
      this.restoringDeletion = false;
    }

    clearUserListDataForReload() {
      // List of users to show on the admin page :
      this.userList=[];
      this.fetchingUserList=false;
    }

    clearLocationDataForReload() {
      // A list of the locations relevant to the currently logged in user.
      this.currentLocation=null;
      this.userLocations=[];
      this.fetchingLocationData=false;
    }

    clearBeaconDataForReload(){
      this.customer_beacons=[];
      this.fetchingBeaconData=false;
    }

    // Compute the list of possible parent locations
    @computed get parentLocationChoices() {
      var result = [];
      function addChildrenToList(location) {
        // Leaf nodes in location tree (plain rooms) cannot be parents:
        if(location.id_type=='R') return;
        result.push(location);
        location.children.forEach(addChildrenToList);
      }
      this.userLocations.forEach(addChildrenToList);
      return result;
    }

    // Get a list of all the leaf-node-rooms in the location tree
    allRooms() {
      var rooms = [];
      function findRooms(location) {
        // Leaf nodes in location tree (plain rooms) cannot be parents:
        if(location.id_type=='R') {
          rooms.push(location);
          return;
        }
        location.children.forEach(findRooms);
      }
      this.userLocations.forEach(findRooms);
      return rooms;
    }

    // If Rooms have a NULL deafult job type, and there are more than
    // once choice for this customer, set the first choice as the default
    // for the room.
    // This method is run after having loaded available job types, and
    // after having loaded locations. (As we don't know which one will
    // be loaded first)
    fixDefaultJobTypesForRooms() {
      if(this.availableJobTypes.length<2) return;
      var defaultJobType=this.availableJobTypes[0];
      this.allRooms().forEach( location => {
        if(location.default_job_type == null) {
          location.default_job_type=defaultJobType.id;
        }
      })
      // Then we check the new Room location needs fixing:
      if(this.newRoomForm.location.default_job_type == null) {
        this.newRoomForm.location.default_job_type = defaultJobType.id;
      }
    }

    // When we fetch a new job that we may have already.. :
    insertOrUpdateJob(job) {
      var previous = this.allJobsIndex()[job.key];
      if(previous) {
        console.log("Merging data into existing job");
        previous.updateFrom(job);
        return previous;
      }
      else {
        this.jobList.push(job);
        return job;
      }
    }
    // When we fetch a machinethat we may have already.. :
    insertOrUpdateMachine(machine) {
      var previous = this.findMachine(machine.id)
      if(previous) {
        previous.updateFrom(machine);
        return previous;
      }
      else {
        this.machineList.push(machine);
        return machine;
      }
    }

    // Get a key->job map.  Used to find jobs we already know of.
    // We have two lists of jobs, the jobList and then the last_job of
    // each machine we know of.  This is due to the fact that we are
    // displaying the busy status for all available machines, even if the
    // job they are currently running is outside the location domain of the
    // user.  jobList only holds jobs running in users location domain.
    domainJobsIndex() {
      var res = {};
      this.jobList.forEach((job) => {
        res[job.key]=job;
      });
      return res;
    }

    allJobsIndex() {
      var res = {};
      this.jobList.forEach((job) => {
        res[job.key]=job;
      });
      this.machineList.forEach((m) => {
        if(m.last_job!=null && !(m.last_job.key in res)) {
          res[m.last_job.key]=m.last_job;
        }
      });
      return res;
    }
    // Look through all locations until the one with the id is found:
    findLocationWithId(id) {
      // Helper function for recursive tree-search:
      function findFromNode(node,id) {
        if(node.id==id) return node;
        if(!node.children || node.children.length==0) return null;
        for(var i=0;i<node.children.length;i++) {
          var child=node.children[i];
          // If it finds something else than undefined, we take it :
          var found = findFromNode(child,id);
          if(!!found) return found;
        };
        return null;
      }
      // Look at all start (root) locations:
      for(var i=0;i<this.userLocations.length;i++) {
        var rootnode=this.userLocations[i];
        // If it finds something else than undefined, we take it :
        var found = findFromNode(rootnode,id);
        if(!!found) return found;
      };
      return null;
    }
    node_env() {
      return process.env.NODE_ENV;
    }
    version() {
      return process.env.VERSION;
    }
    @computed get config() {
      var configs = {
        development:{
          AUTH0_CLIENT_ID:'FAlb2gAEFtVfsi0Klk45ooBKTmHchCm0',
          AUTH0_DOMAIN:'app45574311.auth0.com'
        },
        test:{
          AUTH0_CLIENT_ID:'FAlb2gAEFtVfsi0Klk45ooBKTmHchCm0',
          AUTH0_DOMAIN:'app45574311.auth0.com'
        },
        production:{
          AUTH0_CLIENT_ID:'Ryi90epRP6qt33AoFwKlFlkw44DI6kOF',
          AUTH0_DOMAIN:'login.deconx.com'
        }
      }
      return configs[process.env.NODE_ENV];
    }

    // If the user actively refreshes the machine/job data to e.g. poll for
    // new running jobs :
    resetMachineAndJobData() {
      this.machineList=[];
      this.currentMachine=null;
      this.currentMachineLoading=null;
      this.fetchingMachineList=false;
      this.jobList=[];
      this.sortedJobList=[];
      this.fetchingJobList=false;
      this.fetchingMoreJobs=false;
      this.jobListLastFetched = null;
      this.machineListLastFetched=null;
      this.lastFetchedJobListTimestamp=null;
    }

    // If user logs out, we clear all data to make sure logging in as another
    // or the same user will get (re)freshed data
    resetAllData() {
      console.log("Resetting all client side data");
      this.loggedIn=false;
      // We keep the UISessionState as device width was set there when
      // Layout component mounted.  If data needs to be cleared there,
      // add a resetData() method on UISessionState and keep the device width
      // this.uiSession = new UISessionState();
      this.title="Decon-X";
      this.selectedNavLinkIndex="home";
      this.id_customer=null;
      this.id_user=null;
      this.impersonator = null;
      this.permissions=[];
      this.roles=[];
      this.uiMessage=null;
      this.uiUndoAction=null;
      this.uiError=null;
      this.users_prefs = new UsersPreferences();
      this.customerList=[];
      this.customerListIncludingOwnCompany=[];
      this.customerListLoading=false;
      this.machineList=[];
      this.currentMachine=null;
      this.currentMachineLoading=null;
      this.fetchingMachineList=false;
      this.fetchingMachineDetails=false;
      this.machineListLastFetched=null;
      this.jobList=[];
      this.sortedJobList=[];
      this.fetchingJobList=false;
      this.fetchingMoreJobs=false;
      this.jobListLastFetched = null;
      this.lastFetchedJobListTimestamp=null;
      this.userList=[];
      this.fetchingUserList=false;
      this.auditLog=[];
      this.fetchingAuditLog=false;
      this.fetchingDashboardData=false;
      this.currentLocation=null;
      this.userLocations=[];
      this.fetchingLocationData=false;
      this.customer_beacons=[];
      this.fetchingBeaconData=false;
      this.fetchingLocationTypes=null;
      this.newRoomForm = new RoomForm();
      this.updateRoomForm = new RoomForm();
      this.userInfo=null;
      this.timeStampLastJobUpdate = null;
      this.login_email = null;
      this.login_email_verified = false;
      this.missingUserProfile=false;
      this.id_admin_customer=null;
      this.grantableRoles=[];
      this.fetchingGrantableRoles = false;
      this.fetchingEditUserDetails = false;
      this.newUserForm = new UserForm();
      this.updateUserForm = new UserForm();
      this.auditLogSearchForm = new AuditLogSearchForm();
      this.fetchingImpersonateToken=false;
      this.updateCustomerForm = new CustomerForm();
      this.newCustomerForm= new CustomerForm();
      this.customerList=[];
      this.customerListLoading=false;
      this.fetchingCustomerDetails = false;
      this.updatingCustomerDetails = false;
      this.customer_segments = [];
      this.fetching_customer_segments = false;
      this.job_types = [];
      this.fetching_job_types = false;
      this.fetchingAvailableJobTypes=false;
      this.availableJobTypes=[];
      this.fetchingSupportedCountries=false;
      this.supportedCountries=[];
      this.fetchingSupportedLanguages=false;
      this.supportedLanguages = [];
      this.checkingCustomerId=false;
      this.machineSearchForm = new MachineSearchForm();
      this.runningMachineSearch = false;
      this.machineSearchResult = [];
      this.machineAssignmentForm = new MachineAssignmentForm();
      this.fetchingMachineName = false;
      this.customerMachineNames = new Map();
      this.updatingMachineAssignment=false;
      this.contractListLoading = false;
      this.contractDetailLoading = false;
      this.contractForm = new ContractForm();
      this.fetchingContractFormContext = false;
      this.updatingContractData = false;
      this.changingSIMCardActivationStatus = false;
      this.fetchingSimCards = false;
      this.simCards = [];
      this.simCardForm = new SimCardForm();
      this.dataSubscriptions = [];
      this.smsSubscriptions = [];
      this.roamingStatus = null;
      this.simCardUsage = [];
      this.fetchingSimCardDetails = false;
      this.simCardLocationData = null;
      console.log("All data reset");
    }

    // If there are Job objects attached to Machine objects as .last_job
    // attributes that are overlapping with another object in the Job table:
    verifyJobListForLastJobs() {
      // For each machine that we have in our list, we look at their last_job
      // since that object may have been created together with the machine obj.
      // ( Not all jobs connected to a machine-object belongs in the jobList
      //   Since they may not be from the users own location domain.)
      this.machineList.forEach( (m) => {
        if(m.last_job) {
          // Do we have an object in our jobList representing this job ?
          var jobIndex = this.jobList.findIndex( (j) => j.key == m.last_job.key );
          if(jobIndex!=-1) {
            // If it isn't the same object, we fix the references:
            if(this.jobList[jobIndex]!==m.last_job) {
              // Hold on to the job object that was in the jobList :
              var currJobEntry = this.jobList[jobIndex];
              // We just replace the one in the job-list with the one that
              // will be kept up to date:
              this.jobList[jobIndex]=m.last_job;
              // Some fields (such as location is not fetched as part of
              // fetching the last_job of a machine entry)
              // So we copy over fields from the object that used to be in the
              // joblist
              m.last_job.updateFrom(currJobEntry);
            }
          }
          else {
            // If the job belongs to the current user, we stick it in the
            // joblist
            if(m.last_job.user_id == this.id_user
              ||
              (this.userInfo && this.userInfo.id_home_location==null)) {
              this.jobList.push(m.last_job)
            }
            // TODO: If the user does have a home location:
            // Check whether the job is in
            // the users location domain, if so, also add it.
          }
        }
      })
    }

    // Only true if one of the jobs in the jobList (not machine.last_job)
    // is running (which means the job is in the users location domain)
    @computed get hasRunningAreaJob() {
        // Either we have a running job in our jobList
        if (appState.jobList.some( job => job.running )) return true;
        // If one of the running jobs connected to the machines were started
        // by the current user :
        return appState.machineList.some( m => (
          !!m.last_job
          && m.last_job.running
          && m.last_job.user_id == appState.id_user
        ));
    }
    // returns true if there are jobs that should show up on dashboard page:
    @computed get hasRunningOrRecentAreaJob() {
        // If we have a running or recently active job in our jobList
        if (appState.jobList.some( job =>
          (job.running || job.recentlyActive) && !job.timedOut
        )) return true;
        // If one of the running jobs connected to the machines were started
        // by the current user :
        return appState.machineList.some( m => (
          !!m.last_job
          && (m.last_job.running || m.last_job.recentlyActive)
          && m.last_job.user_id == appState.id_user
        ));
    }
    // Computed property,true if some job is still running
    @computed get hasRunningJob() {
        // Either we have a running job in our jobList
        if(appState.jobList.some( job => job.running )) return true;
        // Or a job connected to a machine as its last-job is running:
        //
        // Not sure if we really have to test the line above or if
        // just looking at the last-jobs of the machine-list will always
        // be sufficient.  A running job, must be running on one of the Machines
        // hence it should be the last job of that machine.
        return appState.machineList.some( machine => {
          if(!machine.last_job) return false;
          return machine.last_job.running;
        });
    }
    // Computed property,true if some job is still running
    @computed get someJobRequirePolling() {
        // Either we have a running job in our jobList
        if(appState.jobList.some( job => job.running || job.recentlyActive )) return true;
        // Or a job connected to a machine as its last-job is running:
        //
        // Not sure if we really have to test the line above or if
        // just looking at the last-jobs of the machine-list will always
        // be sufficient.  A running job, must be running on one of the Machines
        // hence it should be the last job of that machine.
        return appState.machineList.some( machine => {
          if(!machine.last_job) return false;
          return machine.last_job.running || machine.last_job.recentlyActive ;
        });
    }
    // returns list of machine-ids to poll for more measurements
    @computed get machineIdsToPollForData() {
      var ids = new Set();
      // See if there are jobs on the job-list that needs tracking
      appState.jobList
        .filter( job => job.running || job.recentlyActive )
        .map( job => job.id_machine )
        .forEach ( id => ids.add(id) );
      // And then see if there are jobs running on any of the machines
      // (possibly not on the job-list as they may be outside location domain)
      appState.machineList
        .filter( m => !!m.last_job &&
          (m.last_job.running || m.last_job.recentlyActive))
        .map( m => m.id )
        .forEach ( id => ids.add(id) );
      //return Array.from(ids);
      return [...ids];
    }
    // returns list of machine-ids with jobs running
    @computed get runningMachineIds() {
      var ids = new Set();
      appState.jobList
        .filter( job => job.running )
        .map( job => job.id_machine )
        .forEach ( id => ids.add(id) );
      appState.machineList
        .filter( m => !!m.last_job && m.last_job.running)
        .map( m => m.id )
        .forEach ( id => ids.add(id) );
      return Array.from(ids);
    }

    // returns map from id to a customer object (assuming customer list is loaded)
    @computed get customerIdMap() {
      var idMap = new Map();
      appState.customerList.forEach ( c => idMap.set(c.id,c) );
      return idMap;
    }
    @computed get customerSelectList() {
      return appState.customerList.map( customer =>
        ({
          "text":customer.name+" ("+customer.id+")",
          "value":customer
        })
      );
    }
    @computed get customerSelectListWithOwnCompany() {
      return appState.customerListIncludingOwnCompany.map( customer =>
        ({
          "text":customer.name+" ("+customer.id+")",
          "value":customer
        })
      );
    }

    @computed get sortedMachineList() {
        console.log("sortedMachineList() called");
        var resList = this.machineList.slice();
        resList.sort((a,b) => {
          return a.id.localeCompare(b.id);
        });
        return resList;
    }

    sortJobList() {
        console.log("sortedJobList() called");
        var resList = this.jobList.slice();
        resList.sort((a,b) => {
          return b.start_time.getTime() - a.start_time.getTime();
        });
        this.sortedJobList=resList;
    }

    // returns TRUE if all the job objects in the jobList have a load_level
    // of at least LIST.
    @computed get jobsLoadedToListLevel() {
      return ! (this.jobList.some( (j) => j.load_level < LoadLevel.LIST))
    }

    // returns true if all the jobs in the job list correspond to what is
    // registered as last_job on the machines in the machineList.
    // This is pretty clear indication that we haven't loaded all jobs for
    // the area yet.
    @computed get hasOnlyMachineLastJobs() {
      var machineJobKeys = new Set();
      this.machineList.forEach((m)=>{
        if(m.last_job) { machineJobKeys.add(m.last_job.key); }
      });
      // Is any of the jobs in the joblist not one of the machine-last-jobs?
      return !this.jobList.some( (j) => !machineJobKeys.has(j.key) );
    }


    findMachine(machine_id) {
      var foundMachine = this.machineList.find(
        (machine) => {return machine.id == machine_id}
      );
      return foundMachine;
    }
    // Finds a pre-loaded machine or inserts a placeholder machine with only
    // id field set to be loaded.
    setCurrentMachine(id_machine) {
      var found = this.findMachine(id_machine);
      if(found) {
        this.currentMachine = found;
      }
      else {
        this.currentMachine = new Machine();
        this.currentMachine.id=id_machine;
        this.machineList.push(this.currentMachine);
      }
      return this.currentMachine;
    }
    // Sets the current location (which is the one shown on location
    // detail/edit page.
    setCurrentLocation(id_location) {
      // console.log("Finding location with id = "+id_location);
      var found = this.findLocationWithId(id_location);
      this.currentLocation = found;
    }
    // ------------------------------------------------------------------------
    //  Computed properties used to configure UI appearances
    // ------------------------------------------------------------------------

    @computed get isImpersonated() {
      return this.impersonator!=null && this.impersonator.id_user!=null;
    }

    @computed get betaTester() {
      // Whether we show the add machine button or not, depends on the
      // permissions of the logged in user.
      return this.roles.map ( r => r.id_role ).indexOf("BETA-TESTER")>-1;
    }
    @computed get isSystemAdmin() {
      // Whether we show the audit log menu item, depends on the
      // role of the logged in user.
      console.log(this.roles);
      return this.roles.map ( r => r.id_role ).indexOf("SYSTEM_ADMIN")>-1;
    }
    @computed get canAdminCustomers() {
      // Whether we can administrate the customers or not, depends on the
      // permissions of the logged in user.
      return this.permissions.indexOf("EDIT_CUSTOMER")>-1;
    }
    @computed get canAddNewCustomers() {
      // Whether we show the add new customers button or not, depends on the
      // permissions of the logged in user.
      return this.permissions.indexOf("EDIT_CUSTOMER")>-1;
    }
    @computed get canImpersonateUsers() {
      // If this user is allowed to log in as another user.
      return this.permissions.indexOf("IMPERSONATE")>-1;
    }
    @computed get canAdminUsers() {
      // Whether we show the add machine button or not, depends on the
      // permissions of the logged in user.
      return this.permissions.indexOf("EDIT_USERS")>-1;
    }
    // TODO : Add a field on the userInfo returned from server
    // that indicates whether this logged in user is part of the
    // service organisation and use that to test here in stead of hard-coding
    @computed get canAdminSIMs() {
      // Whether we show the add machine button or not, depends on the
      // permissions of the logged in user.
      return this.permissions.indexOf("EDIT_USERS")>-1 &&
      this.userInfo && this.userInfo.serviceOrgMember;
    }
    @computed get canRegisterMachines() {
      // Whether we show the add machine button or not, depends on the
      // permissions of the logged in user.
      return this.permissions.indexOf("EDIT_MACHINES")>-1;
    }
    @computed get canAllocateMachines() {
      // If the user can allocate machines to customer
      return this.permissions.indexOf("ALLOCATE_MACHINES")>-1;
    }
    @computed get canEditLocations() {
      // If the user can edit locations, they can add and modify the
      // area structure.
      return this.permissions.indexOf("EDIT_LOCATIONS")>-1;
    }
    @computed get canEditRooms() {
      // If the user can edit rooms, they can add locations of type room
      return this.permissions.indexOf("EDIT_ROOMS")>-1;
    }
    @computed get allowAddingRootLocations() {
      // If this user's home_location is NULL, then the user may add
      // new locations to the root of their customer-domain.
      if(this.userInfo==null) { return false; }
      if(typeof(this.userInfo.id_home_location)=="undefined") { return false; }
      return this.userInfo.id_home_location==null
      || this.userInfo.id_home_location=='root'
      ;
    }

    @computed get sortedSimCards() {
      let simcards = this.simCards;
      if(this.searchTerm) {
        simcards = simcards.filter(sim => sim.iccid.includes(this.searchTerm) || 
                                          sim._name.includes(this.searchTerm) || 
                                          sim._machineId.includes(this.searchTerm) || 
                                          sim._data.includes(this.searchTerm) || 
                                          sim._sms.includes(this.searchTerm))
      }
      return simcards.sort((a,b) => { 
        if (a[this.sortField] < b[this.sortField]) {
          return this.direction == true ? -1 : 1;
        }
        if (b[this.sortField] < a[this.sortField]) {
          return this.direction == true ? 1 : -1;
        }
        return 0; 
      })
    }

    @computed get distributorCustomerId(){
      if(this.nextCustomerId) {
        return `${this.id_customer}-${this.nextCustomerId}` 
      }
      if(this.customerList.length>0){
        return `${this.id_customer}-${this.customerList.length+1}`
      }
      return null
    }

    // Debug Utilities, to be used from the browser command line:
    // Items = an array of the same type of object
    show(items, columns) {
      if(!items) return;
      if(!items.length) return;
      var objAttrs= Object.keys(items[0].constructor.prototype)
      var attrs=columns ? columns : objAttrs;
      if(columns) {
        console.log("Showing ",attrs, "of ",objAttrs);
      }
      var table_rows = items.map( i => {
        var row={};
        attrs.forEach( attr => {
          if(isNaN(i[attr]) || i[attr] instanceof Date || !i[attr]) {
            row[attr] = i[attr]
          }
          else {
            console.log(i[attr]);
            row[attr] = i[attr].toFixed(3);
          }
        });
        return row;
      });
      console.table(table_rows);
    };

    show_jobs() {
      this.show(this.jobList,['id_machine','machine_name','start_time','user_id','status','minutesSinceEnd'])
    }
    show_machines() {
      this.show(this.machineList,["id", "name", "id_customer",  "last_job", "last_job_start_time"])
    }
    show_rooms() {
      this.show(this.allRooms(),["id", "name", "type",  "default_job_type"])
    }
    show_segments() {
      this.show(this.customer_segments,["id", "description"])
    }
    show_job_types() {
      this.show(this.job_types,["id", "name"])
    }
    show_countries() {
      this.show(this.supportedCountries,["country_code", "name"])
    }

    show_data() {
      this.show_jobs();
      this.show_machines();
      this.show_segments();
      this.show_job_types();
      this.show_countries();
    }
};

// Singleton application state
const appState = new AppState();

// Put the application state in a global var to ease debugging
console.log("Setting global appstate to Window to make it reachable through browser console");
window.appState = appState;
window.toJS = toJS;
// Exporting a set of objects that gives access to the DataModel
export default appState;


/** WEBPACK FOOTER **
 ** ./src/DataModel/AppModel.js
 **/